import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import {
  Box,
  Button,
  Card,
  Center,
  CloseButton,
  Group,
  Loader,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconAlertCircle } from '@tabler/icons-react';
import { Grid, List, Lock, Search, Users } from 'react-feather';
import {
  coachPortalApi,
  type CoachCohortDetail,
  type CoachCohortGroup,
  type CoachCohortParticipant,
} from '../../../services/api/coachPortal';
import type { ColumnConfig, SortConfig } from '../../../hooks';
import {
  ButtonToggle,
  ConfigurableTable,
  GroupBadge,
  ParticipantAvatar,
  renderTextCell,
} from '../../../components/common';
import {
  WithdrawnPill,
  renderAttendance,
  renderSessions,
  renderStatus,
} from '../../cohorts/components/participants/ParticipantCellRenderers';
import { CoachGroupsTabContent } from './CoachGroupsTabContent';
import { CoachParticipantDetailModal } from './CoachParticipantDetailModal';
import classes from './CoachParticipantsTabContent.module.css';

export type CoachParticipantsView = 'participants' | 'groups';

// MED-1367: fixed columns. No column picker and no saved column settings,
// so the CM table preferences are never shared with the coach view.
const COLUMNS: ColumnConfig[] = [
  { id: 'name', label: 'Name', visible: true, sortable: true },
  { id: 'email', label: 'Email', visible: true, sortable: true },
  { id: 'role', label: 'Role', visible: true, sortable: true },
  {
    id: 'business_unit',
    label: 'Business unit',
    visible: true,
    sortable: true,
  },
  { id: 'location', label: 'Location', visible: true, sortable: true },
  { id: 'group', label: 'Group', visible: true, sortable: true },
  { id: 'experience', label: 'Experience', visible: true, sortable: false },
  { id: 'primary_goal', label: 'Value', visible: true, sortable: false },
  { id: 'attendance', label: 'Attendance', visible: true, sortable: true },
  { id: 'sessions', label: 'Sessions', visible: true, sortable: true },
  { id: 'status', label: 'Status', visible: true, sortable: true },
];

const DEFAULT_PAGE_SIZE = 25;

// MED-1367: the coach drags a header edge to resize a column. Each text
// column truncates at its column width (full text on hover and in the
// detail modal), because an auto-layout table otherwise grows every column
// to its longest value. The defaults give the text columns room to read;
// the table scrolls sideways when the columns do not fit.
const DEFAULT_COLUMN_WIDTH: Record<string, number> = {
  name: 200,
  email: 220,
  role: 180,
  business_unit: 170,
  location: 150,
  group: 110,
  experience: 260,
  primary_goal: 260,
  attendance: 150,
  sessions: 80,
  status: 110,
};

// On a phone the sticky Name column is narrower and the name wraps.
const PHONE_NAME_WIDTH = 160;

// MED-1367: the narrowest each column can get by drag, so the avatar,
// attendance bar and pills stay whole.
const MIN_COLUMN_WIDTH: Record<string, number> = {
  name: 120,
  group: 90,
  attendance: 140,
  sessions: 60,
  status: 100,
};
const DEFAULT_MIN_COLUMN_WIDTH = 70;

// Cell padding (10px each side), and the avatar plus gap in the Name cell.
const CELL_PADDING = 20;
const NAME_AVATAR_SPACE = 44;

// MED-1367: the coach's column widths persist in this browser, for every
// cohort. A coach-only key, so the staff table settings are never touched.
// The Name width is not saved, because it depends on the screen size.
const COLUMN_WIDTHS_STORAGE_KEY = 'coach_participants_column_widths';

function loadSavedColumnWidths(): Record<string, number> {
  try {
    const raw = window.localStorage.getItem(COLUMN_WIDTHS_STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const widths: Record<string, number> = {};
    for (const [id, value] of Object.entries(parsed)) {
      if (
        id !== 'name' &&
        id in DEFAULT_COLUMN_WIDTH &&
        typeof value === 'number' &&
        Number.isFinite(value)
      ) {
        widths[id] = value;
      }
    }
    return widths;
  } catch {
    return {};
  }
}

function saveColumnWidths(widths: Record<string, number>) {
  try {
    const saved = { ...widths };
    delete saved.name;
    window.localStorage.setItem(COLUMN_WIDTHS_STORAGE_KEY, JSON.stringify(saved));
  } catch {
    // Storage can be full or blocked. The widths then last for this visit.
  }
}

// Rough width of 13px text, used only to decide when to offer the tooltip.
const AVG_CHAR_WIDTH = 7;

/** A text cell cut at maxWidth, with the full text on hover when cut. */
function renderTruncatedText(value: string | null, maxWidth: number) {
  if (!value) return renderTextCell(null);
  return (
    <Tooltip
      label={value}
      disabled={value.length * AVG_CHAR_WIDTH <= maxWidth}
      position="top-start"
      multiline
      maw={300}
    >
      <Text fz={13} truncate="end" maw={maxWidth}>
        {value}
      </Text>
    </Tooltip>
  );
}

// A Mantine Badge label hides overflow, so in a tight column the pill
// shrinks and cuts its text ("WITHDR..."). This wrapper keeps the pill at
// its full width, and the column grows to fit it.
const FULL_WIDTH_PILL: CSSProperties = { minWidth: 'max-content' };

// users.first_name and last_name can be null; never render "null".
function fullName(p: CoachCohortParticipant): string {
  return `${p.first_name ?? ''} ${p.last_name ?? ''}`.trim();
}

function sortValue(
  p: CoachCohortParticipant,
  key: string
): string | number | null {
  switch (key) {
    case 'name':
      return fullName(p).toLowerCase() || null;
    case 'email':
      return p.email.toLowerCase();
    case 'role':
      return p.role?.toLowerCase() || null;
    case 'business_unit':
      return p.business_unit?.toLowerCase() || null;
    case 'location':
      return p.location?.toLowerCase() || null;
    case 'group':
      return p.group_name.toLowerCase();
    case 'attendance':
      return p.attendance_percentage;
    case 'sessions':
      return p.sessions_attended;
    case 'status':
      return p.status;
    default:
      return null;
  }
}

function compareParticipants(
  a: CoachCohortParticipant,
  b: CoachCohortParticipant,
  sort: SortConfig
) {
  const av = sortValue(a, sort.key);
  const bv = sortValue(b, sort.key);
  // Empty values always sort last, in either direction.
  if (av === null && bv === null) return 0;
  if (av === null) return 1;
  if (bv === null) return -1;
  const result =
    typeof av === 'number' && typeof bv === 'number'
      ? av - bv
      : String(av).localeCompare(String(bv), undefined, { numeric: true });
  return sort.order === 'ASC' ? result : -result;
}

function matchesSearch(p: CoachCohortParticipant, query: string) {
  return [
    p.first_name,
    p.last_name,
    p.preferred_name,
    p.email,
    p.role,
    p.business_unit,
    p.location,
    p.group_name,
  ].some(value => value?.toLowerCase().includes(query));
}

function renderNameCell(
  p: CoachCohortParticipant,
  wrapName: boolean,
  columnWidth: number,
  onView: (p: CoachCohortParticipant) => void
) {
  const nameMaxWidth = Math.max(
    columnWidth - CELL_PADDING - NAME_AVATAR_SPACE,
    40
  );
  return (
    <Group gap="sm" wrap="nowrap">
      <ParticipantAvatar
        firstName={p.first_name ?? ''}
        lastName={p.last_name ?? ''}
        size={32}
        style={p.status === 'withdrawn' ? { opacity: 0.5 } : undefined}
      />
      {/* The pill sits under the name, so the narrow column never cuts it. */}
      <Stack gap={2} align="flex-start">
        {/* MED-1367: the name is a button, so the keyboard can open the
            detail modal too. A click on the rest of the row also opens it. */}
        <UnstyledButton
          className={classes.nameButton}
          aria-label={`View details for ${fullName(p) || p.email}`}
          onClick={event => {
            event.stopPropagation();
            onView(p);
          }}
        >
          {wrapName ? (
            <Text fw={500} fz={13} maw={nameMaxWidth}>
              {fullName(p) || p.email}
            </Text>
          ) : (
            <Tooltip
              label={fullName(p) || p.email}
              disabled={
                (fullName(p) || p.email).length * AVG_CHAR_WIDTH <= nameMaxWidth
              }
              position="top-start"
            >
              <Text fw={500} fz={13} truncate="end" maw={nameMaxWidth}>
                {fullName(p) || p.email}
              </Text>
            </Tooltip>
          )}
        </UnstyledButton>
        {p.status === 'withdrawn' && (
          <Box style={FULL_WIDTH_PILL}>
            <WithdrawnPill withdrawnAt={p.withdrawn_at} />
          </Box>
        )}
      </Stack>
    </Group>
  );
}

function renderCell(
  p: CoachCohortParticipant,
  column: ColumnConfig,
  isPhone: boolean,
  columnWidth: number,
  onView: (p: CoachCohortParticipant) => void
) {
  switch (column.id) {
    case 'name':
      return renderNameCell(p, isPhone, columnWidth, onView);
    case 'email':
    case 'role':
    case 'business_unit':
    case 'location':
    case 'experience':
    case 'primary_goal':
      return renderTruncatedText(
        p[column.id],
        Math.max(columnWidth - CELL_PADDING, 30)
      );
    case 'group':
      return <GroupBadge groupName={p.group_name} groupId={p.group_id} />;
    case 'attendance':
      return renderAttendance(p);
    case 'sessions':
      return renderSessions(p);
    case 'status':
      return <Box style={FULL_WIDTH_PILL}>{renderStatus(p.status)}</Box>;
    default:
      return renderTextCell(null);
  }
}

// Withdrawn rows use grey text and a faded avatar, the same as the staff
// participants table.
function getRowStyle(p: CoachCohortParticipant): CSSProperties | undefined {
  return p.status === 'withdrawn' ? { color: '#666666' } : undefined;
}

function isNotFound(error: unknown): boolean {
  const err = error as { status?: number; response?: { status?: number } };
  return (err.status ?? err.response?.status) === 404;
}

interface CoachParticipantsTabContentProps {
  cohort: CoachCohortDetail;
  view: CoachParticipantsView;
  onViewChange: (view: CoachParticipantsView) => void;
  // MED-1367: a click on a session in the Groups view opens the session
  // details drawer, the same as the Sessions tab.
  onSessionClick?: (sessionId: string) => void;
}

/**
 * MED-1367: read-only Participants & Groups tab for a coach. Shows only the
 * current members of the groups the coach leads in this cohort.
 */
export function CoachParticipantsTabContent({
  cohort,
  view,
  onViewChange,
  onSessionClick,
}: CoachParticipantsTabContentProps) {
  const [participants, setParticipants] = useState<CoachCohortParticipant[]>(
    []
  );
  const [groups, setGroups] = useState<CoachCohortGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [noAccess, setNoAccess] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const [searchValue, setSearchValue] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  // MED-1367: the participant in the read-only detail modal. The row stays
  // set after close, so the modal keeps its content while it animates out.
  const [selectedParticipant, setSelectedParticipant] =
    useState<CoachCohortParticipant | null>(null);
  const [detailOpened, setDetailOpened] = useState(false);
  const openParticipantDetail = useCallback((p: CoachCohortParticipant) => {
    setSelectedParticipant(p);
    setDetailOpened(true);
  }, []);
  // On a phone the Name column is narrower and the name wraps.
  const isPhone = useMediaQuery('(max-width: 48em)');

  // MED-1367: widths the coach set by drag. A column with no entry uses its
  // default width.
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(
    loadSavedColumnWidths
  );
  const effectiveColumnWidths = useMemo(() => {
    const widths: Record<string, number> = {
      ...DEFAULT_COLUMN_WIDTH,
      ...(isPhone ? { name: PHONE_NAME_WIDTH } : {}),
    };
    return { ...widths, ...columnWidths };
  }, [columnWidths, isPhone]);

  const handleColumnResize = useCallback((columnId: string, width: number) => {
    const min = MIN_COLUMN_WIDTH[columnId] ?? DEFAULT_MIN_COLUMN_WIDTH;
    setColumnWidths(current => {
      const next = { ...current, [columnId]: Math.max(width, min) };
      saveColumnWidths(next);
      return next;
    });
  }, []);

  const handleColumnResetWidth = useCallback((columnId: string) => {
    setColumnWidths(current => {
      const next = { ...current };
      delete next[columnId];
      saveColumnWidths(next);
      return next;
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    setNoAccess(false);
    try {
      const [participantRows, groupRows] = await Promise.all([
        coachPortalApi.getCohortParticipants(cohort.id),
        coachPortalApi.getCohortGroups(cohort.id),
      ]);
      setParticipants(participantRows);
      setGroups(groupRows);
    } catch (error) {
      // A 404 means the coach has no access to this cohort (removed, or the
      // wrong cohort). A coach with no group gets 200 and empty lists.
      if (isNotFound(error)) {
        setNoAccess(true);
      } else {
        console.error('Failed to load participants:', error);
        setLoadFailed(true);
      }
      setParticipants([]);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [cohort.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSort = useCallback((key: string) => {
    setSortConfig(current => {
      if (current?.key === key) {
        return current.order === 'ASC' ? { key, order: 'DESC' } : null;
      }
      return { key, order: 'ASC' };
    });
    setPage(1);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchValue(value);
    setPage(1);
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setPage(1);
  }, []);

  const filtered = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    const rows = query
      ? participants.filter(p => matchesSearch(p, query))
      : [...participants];
    if (sortConfig) rows.sort((a, b) => compareParticipants(a, b, sortConfig));
    return rows;
  }, [participants, searchValue, sortConfig]);

  // Right-edge fade while the table can scroll further right. Its height
  // follows the scroll viewport, so it never covers the pagination bar.
  const viewportRef = useRef<HTMLDivElement>(null);
  const [fadeHeight, setFadeHeight] = useState<number | null>(null);

  const updateFade = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const canScrollRight =
      viewport.scrollLeft + viewport.clientWidth < viewport.scrollWidth - 1;
    setFadeHeight(canScrollRight ? viewport.clientHeight : null);
  }, []);

  const noGroups = !loading && !loadFailed && !noAccess && groups.length === 0;
  const hasData = !loading && !loadFailed && !noAccess && !noGroups;
  // The table mounts only when rows match; otherwise a message shows in its
  // place, so it can never sit off screen in a scrolled table.
  const showTable = view === 'participants' && hasData && filtered.length > 0;

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!showTable || !viewport) return;
    updateFade();
    viewport.addEventListener('scroll', updateFade, { passive: true });
    const observer = new ResizeObserver(updateFade);
    observer.observe(viewport);
    const content = viewport.firstElementChild;
    if (content) observer.observe(content);
    return () => {
      viewport.removeEventListener('scroll', updateFade);
      observer.disconnect();
    };
  }, [showTable, updateFade]);

  const pageRows = useMemo(
    () => filtered.slice((page - 1) * pageSize, page * pageSize),
    [filtered, page, pageSize]
  );

  // The toggle and search row always renders, so the layout does not jump
  // when the data arrives. Only the content below it changes.
  let body: ReactNode;
  if (loading) {
    body = (
      <Center py="xl">
        <Loader size="sm" />
      </Center>
    );
  } else if (loadFailed) {
    body = (
      <Card withBorder p="xl">
        <Stack align="center" gap="md">
          <ThemeIcon size={48} radius="xl" variant="light" color="red">
            <IconAlertCircle size={24} />
          </ThemeIcon>
          <Text fw={600}>Could Not Load Participants</Text>
          <Text size="sm" c="dimmed" ta="center">
            We could not load your participants. Check your connection and try
            again.
          </Text>
          <Button size="sm" onClick={() => void load()}>
            Try again
          </Button>
        </Stack>
      </Card>
    );
  } else if (noAccess) {
    body = (
      <Card withBorder p="xl">
        <Stack align="center" gap="md">
          <ThemeIcon size={48} radius="xl" variant="light" color="gray">
            <Lock size={24} />
          </ThemeIcon>
          <Text fw={600}>No access to this cohort</Text>
          <Text size="sm" c="dimmed" ta="center">
            You are not a coach in this cohort. Contact the Medley team if this
            looks wrong.
          </Text>
        </Stack>
      </Card>
    );
  } else if (noGroups) {
    body = (
      <Card withBorder p="xl">
        <Stack align="center" gap="md">
          <ThemeIcon size={48} radius="xl" variant="light" color="gray">
            <Users size={24} />
          </ThemeIcon>
          <Text fw={600}>No Groups Yet</Text>
          <Text size="sm" c="dimmed" ta="center">
            You see your participants here once you lead a group in this cohort.
            Contact the Medley team if you expect to lead one.
          </Text>
        </Stack>
      </Card>
    );
  } else if (view === 'participants' && filtered.length === 0) {
    body = (
      <Card withBorder p="xl">
        <Stack align="center" gap="xs">
          <Text c="dimmed" ta="center">
            {searchValue
              ? 'No participants match your search'
              : 'No participants in your groups yet'}
          </Text>
          {searchValue && (
            <Button
              variant="subtle"
              size="xs"
              onClick={() => handleSearchChange('')}
            >
              Clear search
            </Button>
          )}
        </Stack>
      </Card>
    );
  } else if (view === 'participants') {
    body = (
      <Box className={classes.tableWrapper}>
        <ConfigurableTable<CoachCohortParticipant>
          data={pageRows}
          columns={COLUMNS}
          getRowKey={p => p.id}
          renderCell={(p, column) =>
            renderCell(
              p,
              column,
              isPhone,
              effectiveColumnWidths[column.id] ?? DEFAULT_MIN_COLUMN_WIDTH,
              openParticipantDetail
            )
          }
          onRowClick={openParticipantDetail}
          sortConfig={sortConfig}
          onSort={handleSort}
          page={page}
          pageSize={pageSize}
          totalCount={filtered.length}
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
          visibleColumnCount={COLUMNS.length}
          stickyFirstColumn
          resizableColumns
          columnWidths={effectiveColumnWidths}
          onColumnResize={handleColumnResize}
          onColumnResetWidth={handleColumnResetWidth}
          getRowStyle={getRowStyle}
          entityLabel="participants"
          viewportRef={viewportRef}
        />
        {fadeHeight !== null && (
          <div
            className={classes.scrollFade}
            style={{ height: fadeHeight }}
            aria-hidden
          />
        )}
      </Box>
    );
  } else {
    body = (
      <CoachGroupsTabContent
        cohort={cohort}
        groups={groups}
        participants={participants}
        onSessionClick={onSessionClick}
      />
    );
  }

  return (
    <Stack gap="md">
      <Group gap="sm">
        <ButtonToggle<CoachParticipantsView>
          options={[
            { value: 'participants', label: 'Participants', icon: List },
            { value: 'groups', label: 'Groups', icon: Grid },
          ]}
          value={view}
          onChange={onViewChange}
        />
        {view === 'participants' && (
          <TextInput
            placeholder="Search participants..."
            aria-label="Search participants"
            leftSection={<Search size={16} />}
            rightSection={
              searchValue ? (
                <CloseButton size="sm" onClick={() => handleSearchChange('')} />
              ) : undefined
            }
            value={searchValue}
            onChange={e => handleSearchChange(e.currentTarget.value)}
            w={280}
            maw="100%"
            disabled={!hasData}
          />
        )}
      </Group>

      {body}

      <CoachParticipantDetailModal
        opened={detailOpened}
        participant={selectedParticipant}
        onClose={() => setDetailOpened(false)}
      />
    </Stack>
  );
}
